import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

import {
  publishedTableSchema,
  sortTables,
  type PublishedTable,
  type Seating,
} from "./schemas";
import type { LiveState } from "./use-seatings";

/**
 * Seat change requests (TNG-94) at
 * events/{e}/seatings/{s}/seat_requests/{attendeeId}.
 *
 * The attendee writes attendeeId, uid, seatingId, fromTableId, toTableId,
 * reason, status OPEN and requestedAt while the seating is published; the
 * rest is written by DECIDE_SEAT_REQUEST, UNPUBLISH_SEATING, DELETE_SEATING
 * and a publish that removes the table asked for. `toTableName` and
 * `seatingName` are kept when the request closes, for a table or seating
 * deleted since.
 */
export const SEAT_REQUEST_STATUSES = [
  "OPEN",
  "ACCEPTED",
  "DECLINED",
  "CANCELLED",
] as const;

export const MAX_SEAT_REQUEST_REASON = 500;

export const seatRequestSchema = z.object({
  id: z.string(),
  attendeeId: z.string(),
  uid: z.string(),
  seatingId: z.string(),
  fromTableId: z.string(),
  toTableId: z.string(),
  reason: z.string().optional(),
  status: z.enum(SEAT_REQUEST_STATUSES),
  // A pending server timestamp reads as null in the local echo of the write.
  requestedAt: z.instanceof(Timestamp).nullable(),
  decidedAt: z.instanceof(Timestamp).nullable().optional(),
  cancelReason: z
    .enum([
      "SEATING_UNPUBLISHED",
      "SEATING_DELETED",
      "TABLE_DELETED",
      "SEAT_CHANGED",
    ])
    .optional(),
  toTableName: z.string().optional(),
  seatingName: z.string().optional(),
});
export type SeatRequest = z.infer<typeof seatRequestSchema>;

const requestsPath = (eventId: string, seatingId: string) =>
  ["events", eventId, "seatings", seatingId, "seat_requests"] as const;

export type OwnSeatRequestState =
  { status: "loading" } | { status: "ready"; request: SeatRequest | null };

/** The attendee's own request in one seating, live. */
export function useOwnSeatRequest(
  eventId: string,
  seatingId: string,
  attendeeId: string,
): OwnSeatRequestState {
  const [state, setState] = useState<OwnSeatRequestState>({
    status: "loading",
  });
  useEffect(() => {
    setState({ status: "loading" });
    return onSnapshot(
      doc(db, ...requestsPath(eventId, seatingId), attendeeId),
      (snapshot) => {
        const result = snapshot.exists()
          ? seatRequestSchema.safeParse({ id: snapshot.id, ...snapshot.data() })
          : null;
        setState({
          status: "ready",
          request: result?.success ? result.data : null,
        });
      },
      // Without the request the card still shows the table.
      () => setState({ status: "ready", request: null }),
    );
  }, [eventId, seatingId, attendeeId]);
  return state;
}

export interface SeatRequestWrites {
  saving: boolean;
  error: string | null;
  submit: (input: {
    fromTableId: string;
    toTableId: string;
    reason: string;
  }) => Promise<boolean>;
  withdraw: () => Promise<boolean>;
}

/**
 * Creating, changing and withdrawing the attendee's own request, always
 * written whole, which the rules require.
 */
export function useSeatRequestWrites(
  eventId: string,
  seatingId: string,
  uid: string,
  attendeeId: string,
): SeatRequestWrites {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useCallback(
    () => doc(db, ...requestsPath(eventId, seatingId), attendeeId),
    [eventId, seatingId, attendeeId],
  );

  const submit = useCallback<SeatRequestWrites["submit"]>(
    async ({ fromTableId, toTableId, reason }) => {
      setSaving(true);
      setError(null);
      try {
        const trimmed = reason.trim();
        await setDoc(ref(), {
          attendeeId,
          uid,
          seatingId,
          fromTableId,
          toTableId,
          ...(trimmed ? { reason: trimmed } : {}),
          status: "OPEN",
          requestedAt: serverTimestamp(),
        });
        return true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Could not send your request",
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [ref, attendeeId, uid, seatingId],
  );

  const withdraw = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await deleteDoc(ref());
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not withdraw your request",
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [ref]);

  return { saving, error, submit, withdraw };
}

/** A request with the seating it belongs to and that seating's tables. */
export interface SeatRequestEntry {
  request: SeatRequest;
  seating: Seating;
  /** The seating's published dinner tables, empty once unpublished. */
  tables: PublishedTable[];
}

export type SeatRequestListState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | {
      status: "success";
      data: SeatRequestEntry[];
      /** Seatings whose requests could not be read; their rows are missing. */
      failed: string[];
    };

/** What the per-seating listeners have read, by seating id. */
interface SeatRequestReads {
  requests: Map<string, SeatRequest[]>;
  tables: Map<string, PublishedTable[]>;
  failed: Set<string>;
}

/**
 * Every seat change request of every seating, with each seating's published
 * dinner tables, live, for the organiser's Requests tab and its badge. One
 * listener per seating on its requests and one on its published tables;
 * events have a handful of seatings.
 *
 * The listeners follow the seating ids only. Renaming or publishing a
 * seating joins its new name and status onto what was already read, and
 * never tears the listeners down and back up.
 *
 * A seating whose listener fails is named in `failed` and the others still
 * show, so one failed read never hides the rest. When the seatings list
 * itself fails, the state is an error, never a lasting "loading".
 */
export function useEventSeatRequests(
  eventId: string,
  seatings: LiveState<Seating>,
): SeatRequestListState {
  const [reads, setReads] = useState<{
    key: string;
    reads: SeatRequestReads;
  } | null>(null);
  const list = seatings.status === "success" ? seatings.data : null;
  const idsKey = list ? list.map((s) => s.id).join("|") : null;

  useEffect(() => {
    if (idsKey === null) return;
    const ids = idsKey === "" ? [] : idsKey.split("|");
    const current: SeatRequestReads = {
      requests: new Map(),
      tables: new Map(),
      failed: new Set(),
    };
    const publish = () =>
      setReads({
        key: `${eventId}:${idsKey}`,
        reads: {
          requests: new Map(current.requests),
          tables: new Map(current.tables),
          failed: new Set(current.failed),
        },
      });
    publish();
    const fail = (seatingId: string) => () => {
      current.failed.add(seatingId);
      publish();
    };
    const stops = ids.flatMap((seatingId) => [
      onSnapshot(
        collection(db, ...requestsPath(eventId, seatingId)),
        (snapshot) => {
          const found: SeatRequest[] = [];
          snapshot.forEach((item) => {
            const result = seatRequestSchema.safeParse({
              id: item.id,
              ...item.data(),
            });
            if (result.success) found.push(result.data);
          });
          current.requests.set(seatingId, found);
          publish();
        },
        fail(seatingId),
      ),
      onSnapshot(
        collection(db, "events", eventId, "seatings", seatingId, "published"),
        (snapshot) => {
          const found: PublishedTable[] = [];
          snapshot.forEach((item) => {
            const result = publishedTableSchema.safeParse({
              id: item.id,
              ...item.data(),
            });
            if (result.success) found.push(result.data);
          });
          current.tables.set(seatingId, sortTables(found));
          publish();
        },
        fail(seatingId),
      ),
    ]);
    return () => stops.forEach((stop) => stop());
  }, [eventId, idsKey]);

  return useMemo<SeatRequestListState>(() => {
    if (seatings.status === "error")
      return { status: "error", error: seatings.error };
    if (list === null) return { status: "loading" };
    // Reads left over from another event or another set of seatings are
    // not these seatings' reads.
    if (!reads || reads.key !== `${eventId}:${idsKey}`)
      return list.length === 0
        ? { status: "success", data: [], failed: [] }
        : { status: "loading" };
    const { requests, tables, failed } = reads.reads;
    const settled = (s: Seating) =>
      failed.has(s.id) || (requests.has(s.id) && tables.has(s.id));
    if (!list.every(settled)) return { status: "loading" };
    return {
      status: "success",
      data: list
        .filter((seating) => !failed.has(seating.id))
        .flatMap((seating) =>
          (requests.get(seating.id) ?? []).map((request) => ({
            request,
            seating,
            tables: tables.get(seating.id) ?? [],
          })),
        ),
      failed: list.filter((s) => failed.has(s.id)).map((s) => s.name),
    };
  }, [seatings, list, reads, eventId, idsKey]);
}
